import { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import dynamic from 'next/dynamic';

import { ExtraServiceSearchViewModel } from '@api/index';
import { useBasketId } from '@hooks/cache';
import { ExtraServiceRequestType } from '@components/views/types';
import { useDevice } from '@contexts/device';
import { callService } from '@api/remote-call';

const HotelExtraServiceFormDesktop = dynamic(
  () => import('./HotelExtraServiceFormDesktop')
);

const HotelExtraServiceFormMobile = dynamic(
  () => import('./HotelExtraServiceFormMobile')
);

export const CANCELLATION_GUARANTEE_TYPE_ID = 11;

export type ExtraServiceInitialFormType = {
  _in: string;
  out: string;
  hotelId: number;
  roomId?: number;
  numberOfPeople: number;
  currency: string;
  hotelPrice: number;
  refundable: boolean;
  provider: any;
  itemId: string;
  data: ExtraServiceSearchViewModel;
};

type ExtraServiceFormProps = {
  onCancel?(): void;
  onSuccess?(): void;
  initialValues: ExtraServiceInitialFormType;
};

const HotelExtraServiceForm = ({
  onCancel,
  onSuccess,
  initialValues,
}: ExtraServiceFormProps) => {
  const { isDesktop } = useDevice();

  const [extraServiceForm, setExtraServiceForm] = useState<
    ExtraServiceRequestType[]
  >([]);

  const [selectedExtraServiceItems, setSelectedExtraServiceItems] = useState(
    initialValues?.data?.extraServices?.map((extraService) => ({
      extraServiceId: extraService.id,
      quantity: 0,
    }))
  );

  const [, refresh] = useState({});
  const basketId = useBasketId();

  useEffect(() => {
    if (initialValues.data) {
      const serviceForms: ExtraServiceRequestType[] = [];
      initialValues.data.extraServices.forEach((extraService) => {
        serviceForms.push({
          id: extraService.id,
          quantity: 1,
          numberOfPeople: initialValues.numberOfPeople,
        });
      });
      setExtraServiceForm(serviceForms);
    }
  }, [initialValues.data]);

  async function processAddHotelExtraService() {
    if (basketId) {
      const selectedExtraServices = extraServiceForm
        ?.filter((extraService) => extraService.quantity)
        ?.map(({ id, ...others }) => ({
          id: id,
          ...others,
        }));

      for (const extraService of selectedExtraServices) {
        try {
          await callService(
            {
              service: 'HotelsService',
              method: 'prebookAddExtraService',
              version: 'v3',
            },
            {
              body: {
                prebookModel: {
                  checkInDate: initialValues._in,
                  checkOutDate: initialValues.out,
                  hotelId: initialValues.hotelId,
                  roomId: initialValues.roomId,
                  numberOfPeople:
                    selectedExtraServiceItems?.find(
                      (item) => item.extraServiceId === extraService.id
                    )?.quantity ?? extraService.numberOfPeople,
                  currency: initialValues?.currency,
                  hotelPrice: initialValues?.hotelPrice,
                  refundable: initialValues?.refundable,
                  provider: initialValues?.provider,
                  extraServices: [extraService],
                },
                basketId,
                parentItemId: initialValues.itemId,
              },
            }
          );
        } catch (e) {
          //
        }
      }
    }
  }

  const addHotelExtraService = async () => {
    await processAddHotelExtraService();
  };

  const { mutate, isLoading: isSubmittingForm } = useMutation(
    ['hotel-prebook-extra-service'],
    addHotelExtraService,
    {
      onSuccess: () => {
        onSuccess?.();
      },
    }
  );
  if (isDesktop === undefined) return null;

  return (
    <>
      {isDesktop ? (
        <HotelExtraServiceFormDesktop
          onCancel={onCancel}
          initialValues={initialValues}
          extraServiceForm={extraServiceForm}
          refresh={refresh}
          setSelectedExtraServiceItems={setSelectedExtraServiceItems}
          isSubmittingForm={isSubmittingForm}
          mutate={mutate}
        />
      ) : (
        <HotelExtraServiceFormMobile
          onCancel={onCancel}
          initialValues={initialValues}
          extraServiceForm={extraServiceForm}
          refresh={refresh}
          setSelectedExtraServiceItems={setSelectedExtraServiceItems}
          isSubmittingForm={isSubmittingForm}
          mutate={mutate}
        />
      )}
    </>
  );
};

export default HotelExtraServiceForm;
